import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ZoomIn, ZoomOut, Move, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';

const ImagePositionEditor = ({ src, position, onPositionChange, aspectRatio = '1/1', label = 'Image' }) => {
  const containerRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const pos = {
    x: position?.x ?? 50,
    y: position?.y ?? 50,
    zoom: position?.zoom ?? 1,
  };

  const setPos = (newPos) => {
    onPositionChange({ ...pos, ...newPos });
  };

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!dragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dx = ((e.clientX - dragStart.x) / rect.width) * -100;
    const dy = ((e.clientY - dragStart.y) / rect.height) * -100;
    const newX = Math.max(0, Math.min(100, pos.x + dx));
    const newY = Math.max(0, Math.min(100, pos.y + dy));
    setPos({ x: Math.round(newX), y: Math.round(newY) });
    setDragStart({ x: e.clientX, y: e.clientY });
  }, [dragging, dragStart, pos]);

  const handleMouseUp = useCallback(() => {
    setDragging(false);
  }, []);

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    setDragging(true);
    setDragStart({ x: touch.clientX, y: touch.clientY });
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!dragging || !containerRef.current) return;
    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    const dx = ((touch.clientX - dragStart.x) / rect.width) * -100;
    const dy = ((touch.clientY - dragStart.y) / rect.height) * -100;
    const newX = Math.max(0, Math.min(100, pos.x + dx));
    const newY = Math.max(0, Math.min(100, pos.y + dy));
    setPos({ x: Math.round(newX), y: Math.round(newY) });
    setDragStart({ x: touch.clientX, y: touch.clientY });
  }, [dragging, dragStart, pos]);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  if (!src) return null;

  return (
    <div className="space-y-4" data-testid="image-position-editor">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <Move className="h-4 w-4" /> {label} - Drag to reposition
        </label>
        <Button
          type="button"
          onClick={() => setPos({ x: 50, y: 50, zoom: 1 })}
          variant="outline"
          size="sm"
          className="text-xs"
          data-testid="reset-position-btn"
        >
          <RotateCcw className="h-3 w-3 mr-1" /> Reset
        </Button>
      </div>

      {/* Preview Area */}
      <div
        ref={containerRef}
        className="relative rounded-xl overflow-hidden border-2 border-sage-300 bg-gray-100 select-none"
        style={{ aspectRatio }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        data-testid="image-drag-area"
      >
        <img
          src={src}
          alt="Preview"
          draggable={false}
          className="w-full h-full pointer-events-none"
          style={{
            objectFit: 'cover',
            objectPosition: `${pos.x}% ${pos.y}%`,
            transform: `scale(${pos.zoom})`,
            transformOrigin: `${pos.x}% ${pos.y}%`,
            cursor: dragging ? 'grabbing' : 'grab',
          }}
        />
        {/* Drag overlay with crosshair indicator */}
        <div
          className="absolute inset-0"
          style={{ cursor: dragging ? 'grabbing' : 'grab' }}
        />
        {/* Position indicator dot */}
        <div
          className="absolute w-3 h-3 bg-white border-2 border-sage-700 rounded-full shadow-lg pointer-events-none"
          style={{
            left: `${pos.x}%`,
            top: `${pos.y}%`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>

      {/* Zoom Slider */}
      <div className="flex items-center gap-3 px-1">
        <ZoomOut className="h-4 w-4 text-gray-500 flex-shrink-0" />
        <input
          type="range"
          min="0.5"
          max="2.5"
          step="0.05"
          value={pos.zoom}
          onChange={(e) => setPos({ zoom: parseFloat(e.target.value) })}
          className="flex-1 h-2 rounded-lg appearance-none bg-sage-200 accent-sage-700"
          data-testid="zoom-slider"
        />
        <ZoomIn className="h-4 w-4 text-gray-500 flex-shrink-0" />
        <span className="text-xs text-gray-500 w-12 text-right font-mono">{Math.round(pos.zoom * 100)}%</span>
      </div>

      {/* Position display */}
      <div className="flex gap-4 text-xs text-gray-400">
        <span>X: {pos.x}%</span>
        <span>Y: {pos.y}%</span>
        <span>Zoom: {Math.round(pos.zoom * 100)}%</span>
      </div>
    </div>
  );
};

export default ImagePositionEditor;

/**
 * Helper: Apply stored image position as inline style
 * Usage: <img style={getImageStyle(imagePosition)} />
 */
export const getImageStyle = (position) => {
  if (!position) return { objectFit: 'cover', objectPosition: 'center center' };
  const x = position.x ?? 50;
  const y = position.y ?? 50;
  const zoom = position.zoom ?? 1;
  return {
    objectFit: 'cover',
    objectPosition: `${x}% ${y}%`,
    transform: zoom !== 1 ? `scale(${zoom})` : undefined,
    transformOrigin: `${x}% ${y}%`,
  };
};
